import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { canonicalUrl, DEFAULT_OG_IMAGE, seoForPath, SITE_NAME, TWITTER_CARD } from '../config/seo'

/**
 * Set or create a <meta> tag by attribute name/value, then set its content.
 * `attr` is `name` for standard/Twitter meta and `property` for Open Graph.
 */
function setMeta(attr: 'name' | 'property', key: string, content: string): void {
  const selector = `meta[${attr}="${key}"]`
  let el = document.head.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/** Set or create the single <link rel="canonical"> and point it at `href`. */
function setCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Keep <head> metadata in sync with the active route during client-side
 * navigation. The authoritative SEO for first paint / crawlers is baked into
 * static HTML at build time (scripts/prerender.mjs); this hook ensures the tab
 * title and meta stay correct as the SPA navigates without a full reload.
 */
export function useSeo(): void {
  const { pathname } = useLocation()

  useEffect(() => {
    const seo = seoForPath(pathname)
    const url = canonicalUrl(seo.path)

    document.title = seo.title
    setMeta('name', 'description', seo.description)
    setCanonical(url)

    setMeta('property', 'og:title', seo.title)
    setMeta('property', 'og:description', seo.description)
    setMeta('property', 'og:url', url)
    setMeta('property', 'og:type', 'website')
    setMeta('property', 'og:site_name', SITE_NAME)
    setMeta('property', 'og:image', DEFAULT_OG_IMAGE)

    setMeta('name', 'twitter:card', TWITTER_CARD)
    setMeta('name', 'twitter:title', seo.title)
    setMeta('name', 'twitter:description', seo.description)
    setMeta('name', 'twitter:image', DEFAULT_OG_IMAGE)
  }, [pathname])
}
